import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { Trash2 } from 'lucide-react'
import {
  getGetApiCompaniesCurrentServiceconnectionsQueryKey,
  getGetApiCompaniesCurrentServiceconnectionsServiceConnectionIdQueryKey,
  useDeleteApiCompaniesCurrentServiceconnectionsServiceConnectionId,
  useGetApiCompaniesCurrentServiceconnectionsServiceConnectionId,
  usePatchApiCompaniesCurrentServiceconnectionsServiceConnectionId,
  usePostApiCompaniesCurrentServiceconnections,
  usePostApiCompaniesCurrentServiceconnectionsServiceConnectionIdTest,
} from '@/api/generated/company-management-user/company-management-user'
import type { ServiceConnectionType } from '@/api/model'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/shadcn/dialog'
import { Button } from '@/components/shadcn/button'
import { Input } from '@/components/shadcn/input'
import { Label } from '@/components/shadcn/label'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/shadcn/tooltip'
import { LoaderGeneral } from '@/components/common/loaders/LoaderGeneral'
import { ConfirmDialog } from '@/components/common/ConfirmDialog'
import { notify } from '@/lib/notify'
import {
  emptyServiceConnectionFormValues,
  getServiceConnectionDefinition,
  type ServiceConnectionFormValues,
} from '../registry'
import { ServiceConnectionTypeIcon } from './ServiceConnectionTypeIcon'

const FORM_ID = 'service-connection-form'

interface Props {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  /** Create mode: the type picked in the create menu. */
  createType?: ServiceConnectionType | null
  /** Edit mode: id of the connection being edited. */
  connectionId?: number | null
}

export function ServiceConnectionDialog({
  isOpen,
  onOpenChange,
  createType,
  connectionId,
}: Props) {
  const { t } = useTranslation(['service-connections', 'common'])
  const queryClient = useQueryClient()
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false)

  const editedId = connectionId ?? null
  const isCreate = editedId == null

  const form = useForm<ServiceConnectionFormValues>({
    defaultValues: emptyServiceConnectionFormValues,
  })
  const { register, handleSubmit, reset, formState } = form

  const { data, isLoading, isError } = useGetApiCompaniesCurrentServiceconnectionsServiceConnectionId(
    editedId ?? 0,
    undefined,
    { query: { enabled: isOpen && editedId != null } },
  )
  const connection = editedId != null ? (data?.data ?? null) : null

  const definition = getServiceConnectionDefinition(createType ?? connection?.type)

  // Prefill on open and whenever the edited connection changes — deliberately not on every
  // background refetch, which would discard what the user is typing.
  useEffect(() => {
    if (!isOpen || !definition) return

    if (isCreate) {
      reset(definition.defaultValues)
    } else if (connection) {
      reset(definition.fromModel(connection))
    }
  }, [isOpen, editedId, connection?.id])

  const invalidateList = () =>
    queryClient.invalidateQueries({
      queryKey: getGetApiCompaniesCurrentServiceconnectionsQueryKey(),
    })

  const { mutate: createConnection, isPending: isCreating } =
    usePostApiCompaniesCurrentServiceconnections({
      mutation: {
        onSuccess: () => {
          notify.success(t('toasts.created'))
          invalidateList()
          handleOpenChange(false)
        },
        onError: (error) => notify.apiError(error, t('toasts.createError')),
      },
    })

  const { mutate: updateConnection, isPending: isUpdating } =
    usePatchApiCompaniesCurrentServiceconnectionsServiceConnectionId({
      mutation: {
        onSuccess: (response) => {
          notify.success(t('toasts.updated'))
          invalidateList()

          // Keep the detail cache in step so reopening the row shows the saved values at once.
          const updated = response.data
          queryClient.setQueryData(
            getGetApiCompaniesCurrentServiceconnectionsServiceConnectionIdQueryKey(updated.id),
            response,
          )
          handleOpenChange(false)
        },
        onError: (error) => notify.apiError(error, t('toasts.updateError')),
      },
    })

  const { mutate: deleteConnection, isPending: isDeleting } =
    useDeleteApiCompaniesCurrentServiceconnectionsServiceConnectionId({
      mutation: {
        onSuccess: () => {
          notify.success(t('toasts.deleted'))
          setIsConfirmingDelete(false)
          invalidateList()
          handleOpenChange(false)
        },
        onError: (error) => notify.apiError(error, t('toasts.deleteError')),
      },
    })

  const { mutate: testConnection, isPending: isTesting } =
    usePostApiCompaniesCurrentServiceconnectionsServiceConnectionIdTest({
      mutation: {
        // A reachable-but-failing service answers 200 with isSuccessful false; only a
        // non-2xx means this API itself failed.
        onSuccess: (response) => {
          const result = response.data
          if (result.isSuccessful) {
            notify.success(t('toasts.testSucceeded'))
          } else {
            notify.error(t('toasts.testFailed'), { description: result.message ?? undefined })
          }
        },
        onError: (error) => notify.apiError(error, t('toasts.testError')),
      },
    })

  const isSaving = isCreating || isUpdating

  const handleOpenChange = (open: boolean) => {
    if (!open) setIsConfirmingDelete(false)
    onOpenChange(open)
  }

  const onValid = (values: ServiceConnectionFormValues) => {
    if (!definition) return

    if (editedId == null) {
      createConnection({ data: definition.toCreatePayload(values) })
    } else {
      updateConnection({ serviceConnectionId: editedId, data: definition.toUpdatePayload(values) })
    }
  }

  const testBlockedReason = editedId == null
    ? t('dialog.test.saveFirst')
    : formState.isDirty
      ? t('dialog.test.unsavedChanges')
      : null

  return (
    <>
      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {definition && <ServiceConnectionTypeIcon icon={definition.icon} className="size-5" />}
              {isCreate ? t('dialog.titleCreate') : t('dialog.titleEdit')}
            </DialogTitle>
            <DialogDescription>{definition ? t(definition.labelKey) : ''}</DialogDescription>
          </DialogHeader>

          <LoaderGeneral
            isLoading={!isCreate && isLoading}
            isError={(!isCreate && isError) || !definition}
            errorMessage={definition ? t('dialog.loadError') : t('dialog.unknownType')}
            className="h-[200px]"
          >
            {definition && (
              <form
                id={FORM_ID}
                onSubmit={handleSubmit(onValid)}
                className="flex flex-col gap-5 py-2"
              >
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="service-connection-name">
                    {t('dialog.name.label')} <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="service-connection-name"
                    placeholder={t('dialog.name.placeholder')}
                    {...register('name', { required: true })}
                  />
                </div>

                <definition.Fields form={form} isCreate={isCreate} />
              </form>
            )}
          </LoaderGeneral>

          <DialogFooter className="sm:justify-between">
            <div>
              {!isCreate && (
                <Button
                  variant="ghost"
                  className="text-destructive hover:text-destructive"
                  onClick={() => setIsConfirmingDelete(true)}
                  disabled={isSaving || isTesting}
                >
                  <Trash2 className="size-4" />
                  {t('dialog.delete.button')}
                </Button>
              )}
            </div>

            <div className="flex items-center gap-2">
              {testBlockedReason ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span tabIndex={0}>
                      <Button variant="outline" disabled>
                        {t('dialog.test.button')}
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>{testBlockedReason}</TooltipContent>
                </Tooltip>
              ) : (
                <Button
                  variant="outline"
                  onClick={() => editedId != null && testConnection({ serviceConnectionId: editedId })}
                  disabled={isTesting || isSaving}
                >
                  {isTesting ? t('dialog.test.running') : t('dialog.test.button')}
                </Button>
              )}

              <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isSaving}>
                {t('buttons.cancel', { ns: 'common' })}
              </Button>
              <Button type="submit" form={FORM_ID} disabled={isSaving || !definition}>
                {t('buttons.save', { ns: 'common' })}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={isConfirmingDelete}
        title={t('dialog.delete.title')}
        description={t('dialog.delete.description', { name: connection?.name ?? '' })}
        confirmLabel={t('buttons.delete', { ns: 'common' })}
        confirmButtonVariant="destructive"
        isLoading={isDeleting}
        onConfirm={() => editedId != null && deleteConnection({ serviceConnectionId: editedId })}
        onClose={() => setIsConfirmingDelete(false)}
      />
    </>
  )
}
